import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Banknote } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

/**
 * Portal Settings → Third-Party Lenders catalog manager.
 * Backs the autocomplete on the Use-of-Funds Consolidation tab so the
 * operator always picks consistent names ("IWOCA" not "iwoca").
 */
export default function ThirdPartyLendersSettings() {
  const [lenders, setLenders] = useState([]);
  const [region, setRegion] = useState("UK");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [removingId, setRemovingId] = useState(null);

  const load = async (reg = region) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/contracts/third-party-lenders?region=${reg}`);
      setLenders(data?.lenders || []);
    } catch (err) {
      toast.error("Could not load lenders: " + errorDetailMessage(err, err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(region);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  const addLender = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Enter a lender name first");
      return;
    }
    try {
      setAdding(true);
      await api.post("/contracts/third-party-lenders", { name, region });
      toast.success(`Added "${name}" (${region})`);
      setNewName("");
      await load();
    } catch (err) {
      toast.error(errorDetailMessage(err, err.message));
    } finally {
      setAdding(false);
    }
  };

  const removeLender = async (lender) => {
    if (!confirm(`Remove "${lender.name}" from the lender catalog?`)) return;
    try {
      setRemovingId(lender.id);
      await api.delete(`/contracts/third-party-lenders/${lender.id}`);
      toast.success(`Removed "${lender.name}"`);
      await load();
    } catch (err) {
      toast.error(errorDetailMessage(err, err.message));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Card data-testid="third-party-lenders-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Banknote className="w-5 h-5 text-emerald-600" />
          Third-Party Lenders Catalog
          <Badge variant="secondary" data-testid="lender-count">{lenders.length}</Badge>
        </CardTitle>
        <CardDescription>
          Names operators can pick from on the Use-of-Funds Consolidation tab. Keeping this list tight ensures customer-facing documents always use the exact same lender name (no "IWOCA" / "iwoca" / "Iwoca Ltd" drift).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Region toggle */}
        <div className="flex gap-1 rounded-lg bg-slate-100 p-1 w-fit" data-testid="lender-region-toggle">
          {["UK", "AUS"].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRegion(r)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                region === r ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`lender-region-${r.toLowerCase()}`}
            >
              {r === "UK" ? "🇬🇧 UK" : "🇦🇺 AUS"}
            </button>
          ))}
        </div>

        {/* Add lender */}
        <div className="flex gap-2">
          <Input
            placeholder={region === "AUS" ? "New AUS lender name (e.g. Prospa)" : "New lender name (e.g. Boost Capital)"}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addLender()}
            disabled={adding}
            data-testid="new-lender-input"
          />
          <Button
            onClick={addLender}
            disabled={adding || !newName.trim()}
            data-testid="add-lender-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Add
          </Button>
        </div>

        {/* Existing lenders */}
        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : lenders.length === 0 ? (
          <p className="text-sm text-slate-500 py-4 text-center">
            No lenders yet — add the first one above.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {lenders.map((lender) => (
              <div
                key={lender.id}
                className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2"
                data-testid={`lender-row-${lender.id}`}
              >
                <span className="text-sm font-medium text-slate-900 truncate">
                  {lender.name}
                </span>
                <button
                  type="button"
                  onClick={() => removeLender(lender)}
                  disabled={removingId === lender.id}
                  className="text-slate-400 hover:text-red-600 disabled:opacity-50"
                  data-testid={`remove-lender-${lender.id}`}
                  title="Remove from catalog"
                >
                  {removingId === lender.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
